import { useCallback, useEffect, useRef, useState } from "react";

interface UseDraggingProps {
  onDragComplete: (dragOffset: number) => void;
  threshold?: number; // for triggering the action.
}

interface DraggingState {
  isDragging: boolean;
  dragOffset: number;
}

const useDragging = ({ onDragComplete, threshold = 10 }: UseDraggingProps) => {
  const [{ isDragging, dragOffset }, setState] = useState<DraggingState>({
    isDragging: false,
    dragOffset: 0,
  });
  const startX = useRef(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return;
      setState((prev) => ({ ...prev, dragOffset: e.clientX - startX.current }));
    };

    const handleMouseUp = () => {
      if (!isDragging) return;

      // Determine the drag direction and complete the drag
      if (Math.abs(dragOffset) > threshold) {
        onDragComplete(dragOffset); // Trigger the provided callback with the drag offset
      }

      // Reset drag state
      setState({ isDragging: false, dragOffset: 0 });
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, dragOffset, onDragComplete, threshold]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    startX.current = e.clientX;
    setState((prev) => ({ ...prev, isDragging: true }));
  }, []);

  return {
    isDragging,
    dragOffset,
    handleMouseDown,
  };
};

export default useDragging;
